import type { Maybe, Prettify } from '@web-platform/shared-universal-types-utils/src/utils';

type IsEmpty<T> = keyof T extends never ? true : false;
type IsNullOrUndefined<T> = NonNullable<T> extends never ? true : false;

type CleanedObject<TObj extends RecursiveRecord> = Prettify<{
  [
    Key in keyof TObj as IsNullOrUndefined<TObj[Key]> extends true
      ? never
      : NonNullable<TObj[Key]> extends object
        ? IsEmpty<CleanedObject<NonNullable<TObj[Key]>>> extends true
          ? never
          : Key
        : Key
  ]-?: NonNullable<TObj[Key]> extends object
    ? CleanedObject<NonNullable<TObj[Key]>>
    : NonNullable<TObj[Key]>;
}>;

type SupportedPrimitives = string | number | boolean;

type RecursiveRecord = {
  [key: string]: Maybe<SupportedPrimitives | RecursiveRecord>;
};

const isNullishOrEmpty = <TVal extends Maybe<SupportedPrimitives | RecursiveRecord>>(
  value: TVal,
): boolean => value == null || (typeof value === 'object' && !Object.keys(value).length);

export const cleanEmpty = <TObj extends RecursiveRecord>(obj: TObj): CleanedObject<TObj> =>
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  Object.entries(obj).reduce((acc: RecursiveRecord, [key, val]) => {
    const cleanedValue = val != null && typeof val === 'object' ? cleanEmpty(val) : val;

    if (!isNullishOrEmpty(cleanedValue)) {
      acc[key] = cleanedValue;
    }

    return acc;
  }, {}) as CleanedObject<TObj>;
